import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ClipboardList, ArrowRight, X as XIcon } from "lucide-react";
import api from "../lib/api";
import { STATUS_COLORS, withAlpha, BRAND_COLORS } from "../lib/bookingStatus";

/**
 * Team-dashboard hero card: total number of open to-dos across every active
 * booking + every open service request.
 *
 * Click on the number → sets `?open=1` on the current URL, which:
 *   • filters the customer + request lists on the dashboard
 *   • propagates to /team/calendar via the "Im Kalender ansehen"-Link
 *
 * The card polls every 15 s so counts stay live even when the concierge
 * ticks items in another tab.
 *
 * Consumers of the filter state:
 *   - TeamDashboard reads the URL param via `useSearchParams` and passes
 *     `bookings_with_open` / `customer_ids_with_open` / `request_ids_open`
 *     to its list-filter memos.
 *   - TeamCalendar reads `?open=1` and hides absence bars whose booking is
 *     `status === "completed"` and task badges where every event is done.
 */
export default function OpenTodosCard({ onSummaryChange }) {
  const [summary, setSummary] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const filterActive = searchParams.get("open") === "1";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/team/open-todos-summary");
      setSummary(data);
      onSummaryChange?.(data);
    } catch (e) {
      console.error("open-todos load failed:", e);
    }
  }, [onSummaryChange]);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const total = summary?.total ?? null;
  const openItems = summary?.total_open_items ?? 0;
  const openRequests = summary?.total_open_requests ?? 0;
  const bookingsCount = summary?.booking_count_with_open ?? 0;

  const toggleFilter = () => {
    const next = new URLSearchParams(searchParams);
    if (filterActive) next.delete("open");
    else next.set("open", "1");
    setSearchParams(next, { replace: false });
  };

  const isZero = total === 0;
  // Feb 2026 · Farbcode strikt nach Systemregel: keine offenen Todos → GRÜN
  // (alles erledigt), offene Todos → BLAU (Arbeit in Bearbeitung). ROT ist
  // exklusiv Problem/Verzögerung vorbehalten — offene Todos sind NORMAL,
  // kein Alarm.
  const accent = isZero ? STATUS_COLORS.green : STATUS_COLORS.blue;
  const soft = isZero ? withAlpha(STATUS_COLORS.green, 0.08) : withAlpha(STATUS_COLORS.blue, 0.08);

  return (
    <div
      className="mb-8 bg-white border border-navy/10 p-6 md:p-8"
      style={filterActive ? { borderColor: STATUS_COLORS.blue, background: withAlpha(STATUS_COLORS.blue, 0.03) } : {}}
      data-testid="open-todos-card"
      data-filter-active={filterActive ? "true" : "false"}
    >
      <div className="flex items-start gap-6 flex-wrap md:flex-nowrap">
        <div
          className="shrink-0 flex items-center justify-center w-14 h-14 rounded-full"
          style={{ background: soft, border: `1px solid ${accent}30` }}
        >
          <ClipboardList className="w-6 h-6" style={{ color: accent }} strokeWidth={1.6} />
        </div>

        <button
          type="button"
          onClick={toggleFilter}
          disabled={total === null}
          className="flex-1 min-w-0 text-left group disabled:opacity-40"
          data-testid="open-todos-toggle-btn"
          title={filterActive ? "Filter aufheben" : "Nur offene Aufgaben anzeigen"}
        >
          <div className="label-caps mb-2" style={{ color: accent }}>
            Offene To-dos
          </div>
          <div className="flex items-baseline gap-3 mb-1">
            <span
              className="font-display text-5xl md:text-6xl leading-none tabular-nums"
              style={{ color: accent }}
              data-testid="open-todos-total"
            >
              {total === null ? "…" : total}
            </span>
            <span className="text-[13px] text-navy-dark/60">
              {total === 1 ? "offene Aufgabe" : "offene Aufgaben"}
            </span>
          </div>
          <div className="text-[12.5px] text-navy-dark/60 mt-2 leading-relaxed max-w-xl">
            {isZero ? (
              <>Aktuell keine offenen Punkte — Ihr Team ist auf dem neuesten Stand.</>
            ) : (
              <>
                <span data-testid="open-todos-breakdown">
                  <strong style={{ color: BRAND_COLORS.navy }}>{openItems}</strong> Buchungs-Punkt(e)
                  {" · "}
                  <strong style={{ color: BRAND_COLORS.navy }}>{openRequests}</strong> Kundenanfrage(n)
                </span>{" "}
                aus {bookingsCount} aktiven Buchung(en).
              </>
            )}
          </div>
          {!isZero && (
            <div
              className="mt-3 inline-flex items-center gap-2 text-[11.5px] tracking-[0.15em] uppercase transition-colors"
              style={{ color: filterActive ? STATUS_COLORS.blue : BRAND_COLORS.goldDark }}
            >
              {filterActive ? (
                <>
                  <XIcon className="w-3.5 h-3.5" strokeWidth={2.2} />
                  Filter aufheben · Alle anzeigen
                </>
              ) : (
                <>
                  Nur offene anzeigen
                  <ArrowRight
                    className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1"
                    strokeWidth={2.2}
                  />
                </>
              )}
            </div>
          )}
        </button>

        {!isZero && (
          <Link
            to={filterActive ? "/team/calendar?open=1" : "/team/calendar?open=1"}
            className="shrink-0 border border-navy text-navy px-5 py-2.5 text-[11.5px] tracking-[0.15em] uppercase hover:bg-navy hover:text-ivory transition-colors self-end md:self-center"
            data-testid="open-todos-calendar-link"
            title="Kalender mit demselben Filter öffnen"
          >
            Im Kalender ansehen →
          </Link>
        )}
      </div>

      {filterActive && !isZero && (
        <div
          className="mt-5 pt-4 border-t text-[12px] text-navy-dark/70"
          style={{ borderColor: withAlpha(STATUS_COLORS.blue, 0.15) }}
          data-testid="open-todos-filter-hint"
        >
          <span className="font-medium" style={{ color: STATUS_COLORS.blue }}>Filter aktiv:</span>{" "}
          Kunden ohne offene Aufgaben und erledigte Anfragen sind unten ausgeblendet.
          Der Kalender-Link nimmt denselben Filter mit.
        </div>
      )}
    </div>
  );
}
